import { AxiosError } from 'axios';
import { Outlet } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';
import { useEffect } from 'react';

import Header from '../Header';
import Footer from '../Footer';
import OngoingEvaluations from '@/components/OngoingEvaluations';

import { Iconlabel } from '@wk/components-react16';

import { getUserRoleAccess } from '@/services';
import useOnGoingEvaluationsStore from '@/stores/ongoingEvaluations';
import useApplicationsStore from '@/stores/applications';
import Toaster from '@/components/ui/Toaster';
import type { ErrorResponse } from '@/types';

const AppLayout = () => {
  const { isLoading, error: applicationsError, fetchApplications } = useApplicationsStore(
    useShallow((state) => ({
      isLoading: state.isLoading,
      error: state.error,
      fetchApplications: state.fetchApplications,
    })),
  );

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  const { isLoading: isUserRoleAccessLoading } = useQuery({
    queryKey: ['userRoleAccess'],
    queryFn: getUserRoleAccess,
  });

  const { isOngoingEvaluationsOpen, setIsOngoingEvaluationsOpen } =
    useOnGoingEvaluationsStore(
      useShallow((state) => ({
        isOngoingEvaluationsOpen: state.isOngoingEvaluationsOpen,
        setIsOngoingEvaluationsOpen: state.setIsOngoingEvaluationsOpen,
      })),
    );

  const renderContent = () => {
    if (applicationsError) {
      const appError = applicationsError as AxiosError<ErrorResponse>;
      return (
        <div className="w-full h-full justify-center items-center flex flex-col gap-2">
          <h2 className="text-lg font-bold">Error</h2>
          <p className="text-sm text-gray-500">
            {appError?.response?.data.title ||
              'An error occurred while fetching application data.'}
          </p>
        </div>
      );
    }

    if (isLoading || isUserRoleAccessLoading) {
      return (
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center">
          <span className="animate-spin">
            <Iconlabel name="spinner-alt" />
          </span>
        </div>
      );
    }
    return <Outlet />;
  };

  return (
    <div className="h-full w-full flex flex-col relative">
      <Header />
      <div className="flex flex-col gap-4 px-4 lg:pr-8 py-4 w-full min-h-0 flex-1">
        {renderContent()}
        <OngoingEvaluations
          isOpen={isOngoingEvaluationsOpen}
          onClose={() => setIsOngoingEvaluationsOpen(false)}
        />
        <Toaster />
      </div>
      <Footer />
    </div>
  );
};

export default AppLayout;
